import type { Timestamp } from '@bufbuild/protobuf/wkt'
import {
  type BiddingBlockApprovalRequest,
  BiddingBlockBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import {
  BiddingApprovalState,
  BiddingBidSendStatus,
  type BiddingJepxReconcileOutcome,
  BiddingSpotBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'

/** id と承認状態・更新日時だけを持つ最小形 (一覧 item / 明細 entity のどちらからも作れる)。 */
type ApprovalStateOf = {
  id: string
  approvalState: BiddingApprovalState
  updatedAt?: Timestamp
  syncUpdatedAt?: Timestamp
  sendStatus?: BiddingBidSendStatus
  /** JEPX 照合の結果 (AC-BSP-117)。 一覧 item も明細も持つ。 */
  jepxReconcileOutcome?: BiddingJepxReconcileOutcome
  createdBy?: string
  /** 明細が取下処理中の札・leg を持つ (= 取下の結果をまだ受け取っていない)。 明細側だけが持つ。 */
  awaitsWithdrawOutcome?: boolean
}

/** 通常入札の明細 (screen 応答) を、一覧 item と比べる最小形にする。 */
export function spotDetailStatesOf(
  requests: readonly BiddingSpotApprovalRequest[],
): ApprovalStateOf[] {
  return requests.map((request) => ({
    id: request.id,
    approvalState: request.approvalState,
    updatedAt: request.updatedAt,
    syncUpdatedAt: request.syncUpdatedAt,
    sendStatus: request.sendStatus,
    jepxReconcileOutcome: request.jepxReconcileOutcome,
    createdBy: request.createdBy,
    awaitsWithdrawOutcome: request.spotBids.some(
      (bid) => bid.bidStatus === BiddingSpotBidStatus.WITHDRAWING,
    ),
  }))
}

/** ブロック入札の明細 (screen 応答) を、一覧 item と比べる最小形にする。 */
export function blockDetailStatesOf(
  requests: readonly BiddingBlockApprovalRequest[],
): ApprovalStateOf[] {
  return requests.map((request) => ({
    id: request.id,
    approvalState: request.approvalState,
    updatedAt: request.updatedAt,
    syncUpdatedAt: request.syncUpdatedAt,
    sendStatus: request.sendStatus,
    jepxReconcileOutcome: request.jepxReconcileOutcome,
    awaitsWithdrawOutcome: [request.primary, request.secondary].some(
      (leg) => leg?.bidStatus === BiddingBlockBidStatus.WITHDRAWING,
    ),
  }))
}

/**
 * 「一覧が持つ承認状態」と「screen 応答が持つ承認状態」が食い違うか、
 * どちらか一方にしか存在しない承認依頼の id を返す。
 *
 * AC-BSP-096 の自動更新は **承認ステータスに対象を限定**していて、承認依頼の明細
 * (価格・数量・追加札) の定期再取得は spec が明示的に対象外にしている (編集中の
 * 下書きを巻き込むため)。 一方で承認状態バナー (AC-BSP-067) が出す承認日時・
 * 差戻し理由・提出日時は一覧 item に無く明細 Get にしか無いので、承認状態が動いた
 * 瞬間だけはバナー用に明細を取り直す必要がある。
 *
 * AC-BSP-125 の要確認の付与・解除は操作日時を変えないため、独立した syncUpdatedAt が
 * 明細より新しい場合も同じ経路で取り直す。明細の方が新しい遅延一覧は無視する。
 *
 * その「動いた瞬間」を、polling している一覧の承認状態と手元の明細の承認状態の
 * 差分として検出するのが本関数。 呼び出し元は screen query を 1 回再取得し、
 * 差分が解消するまで書き込みを fail-close する。
 *
 * 多段階承認の途中の段の承認は、 承認待ちのまま完了段数だけを進める。 完了段数と承認者の
 * 写しの当該段 (AC-BSP-098) は明細にしか無いので、 承認待ち同士は一覧の更新日時が明細より
 * 新しいことも差分に数える (明細の方が新しい時は取り直さない)。
 *
 * 取下 (AC-BSP-057) の結果 (取消 / 取下失敗) は、 提出済のまま明細の札・leg の状態だけを
 * 動かす。 backend は結果を書く時に申請セット・グループの更新日時を進めるので、 取下処理中の
 * 明細を持つ提出済の id は、 一覧の更新日時が明細より新しいことを差分に数える。
 *
 * 送信失敗の確定 (SEND_FAILED_FINAL) は、 提出済のまま同じ書き込みで札を失効にすることがある
 * (AC-BSP-117)。 失効にしたかで送信結果の通知 (AC-BSP-130) と一覧の表示が変わるので、 一覧の
 * 送信状態が送信失敗の確定になり、 明細がまだそうでない提出済の id も差分に数える。
 *
 * JEPX 照合 (AC-BSP-117) は、 送信失敗(確定)で失効にした札を入札中・約定待ちに直し、 内容も
 * JEPX の札に置き換える。 送信状態の変化では明細を取り直さず、 照合は更新日時の比較にも
 * 乗らないことがあるので、 一覧の照合結果が明細のそれと違う id も差分に数える。
 *
 * 提出済の札は、取り込みによる結果更新・発注主体区分の埋め戻しで更新日時だけが進む。
 * 取り込み札に加えて、eBalance から出した JEPX 受理済の札も対象にする。送信状態が変わらず
 * 一覧の更新日時が明細より新しいことを差分に数える (AC-BSP-110a)。送信状態の変化だけでは
 * 明細を取り直さない (#10084)。
 */
export function findStaleApprovalDetailIds(
  listStates: ApprovalStateOf[],
  detailStates: ApprovalStateOf[],
): string[] {
  const detailById = new Map(detailStates.map((detail) => [detail.id, detail]))
  const listStateById = new Map(
    listStates.map((item) => [item.id, item.approvalState]),
  )
  const staleListIds = listStates
    .filter((item) => {
      const detail = detailById.get(item.id)
      return (
        detail === undefined ||
        detail.approvalState !== item.approvalState ||
        isPendingUpdatedSince(detail, item) ||
        isWithdrawOutcomeLanded(detail, item) ||
        isSendFailureConfirmedSince(detail, item) ||
        isFailedFinalSettledSince(detail, item) ||
        isSubmittedUpdatedSince(detail, item) ||
        isSyncUpdatedSince(detail, item) ||
        isReconcileOutcomeChanged(detail, item)
      )
    })
    .map((item) => item.id)
  const staleDetailIds = detailStates
    .filter((detail) => !listStateById.has(detail.id))
    .map((detail) => detail.id)

  return [...staleListIds, ...staleDetailIds]
}

/** 一覧の JEPX 照合の結果が、 明細が持つものと違うか (明細が照合を反映していない)。 */
function isReconcileOutcomeChanged(
  detail: ApprovalStateOf,
  item: ApprovalStateOf,
): boolean {
  return (
    item.jepxReconcileOutcome !== undefined &&
    detail.jepxReconcileOutcome !== item.jepxReconcileOutcome
  )
}

/** 承認待ちの一覧 item が明細より後に更新されたか (どちらかが更新日時を持たなければ比べない)。 */
function isPendingUpdatedSince(
  detail: ApprovalStateOf,
  item: ApprovalStateOf,
): boolean {
  return (
    item.approvalState === BiddingApprovalState.PENDING &&
    isUpdatedSince(detail, item)
  )
}

/** 取下処理中の明細より後に、 提出済の一覧 item が更新されたか (= 取下の結果が着いた)。 */
function isWithdrawOutcomeLanded(
  detail: ApprovalStateOf,
  item: ApprovalStateOf,
): boolean {
  return (
    detail.awaitsWithdrawOutcome === true &&
    item.approvalState === BiddingApprovalState.SUBMITTED &&
    isUpdatedSince(detail, item)
  )
}

/** 提出済の一覧 item の送信が、 明細より後に送信失敗で確定したか。 */
function isSendFailureConfirmedSince(
  detail: ApprovalStateOf,
  item: ApprovalStateOf,
): boolean {
  return (
    item.approvalState === BiddingApprovalState.SUBMITTED &&
    item.sendStatus === BiddingBidSendStatus.SEND_FAILED_FINAL &&
    detail.sendStatus !== BiddingBidSendStatus.SEND_FAILED_FINAL
  )
}

/**
 * 提出済・送信失敗の確定のまま、 明細より後に一覧 item が更新されたか。 JEPX に届いたかを
 * 確かめた結果、 届いていなければ札・leg を失効にする (AC-BSP-117)。 失効の書き込みは親の
 * 更新日時を進めるが一覧の承認軸・送信軸・原因分類は変わらず、 screen は polling しないので、
 * この述語が無いと明細の札が失効したことを画面が目にしない (送信失敗の確定の通知が出ない。
 * AC-BSP-130)。 取下の結果 (`isWithdrawOutcomeLanded`) と同じく、 更新日時で見る。
 */
export function isFailedFinalSettledSince(
  detail: ApprovalStateOf,
  item: ApprovalStateOf,
): boolean {
  return (
    item.approvalState === BiddingApprovalState.SUBMITTED &&
    item.sendStatus === BiddingBidSendStatus.SEND_FAILED_FINAL &&
    detail.sendStatus === BiddingBidSendStatus.SEND_FAILED_FINAL &&
    isUpdatedSince(detail, item)
  )
}

/** 提出済の一覧 item が、送信状態を変えずに明細より後に更新されたか。 */
function isSubmittedUpdatedSince(
  detail: ApprovalStateOf,
  item: ApprovalStateOf,
): boolean {
  return (
    item.approvalState === BiddingApprovalState.SUBMITTED &&
    (item.createdBy === undefined ||
      item.createdBy === '' ||
      item.sendStatus === BiddingBidSendStatus.SENT) &&
    item.sendStatus === detail.sendStatus &&
    isUpdatedSince(detail, item)
  )
}

function isUpdatedSince(
  detail: ApprovalStateOf,
  item: ApprovalStateOf,
): boolean {
  if (detail.updatedAt === undefined || item.updatedAt === undefined) {
    return false
  }
  return (
    item.updatedAt.seconds > detail.updatedAt.seconds ||
    (item.updatedAt.seconds === detail.updatedAt.seconds &&
      item.updatedAt.nanos > detail.updatedAt.nanos)
  )
}

/**
 * 承認状態バナーの見出しは JEPX への送信状態でも決まる (AC-BSP-067)。 送信状態は承認状態と
 * 同じ周期で polling している一覧のほうが明細より新しいことがあるが、 AC-BSP-096 は明細の
 * 再取得を承認ステータスの変化に限っている。 そこで送信状態の変化では明細を取り直さず、
 * 一覧が持つ送信状態を明細に重ねてバナーへ渡す。 一覧が送信状態を持たない (未設定) 間は
 * 明細の値のまま。
 */
export function withListSendStatus<
  T extends { id: string; sendStatus: BiddingBidSendStatus },
>(
  details: readonly T[],
  listItems: ReadonlyArray<{ id: string; sendStatus: BiddingBidSendStatus }>,
): T[] {
  const listSendStatusById = new Map(
    listItems.map((item) => [item.id, item.sendStatus]),
  )
  return details.map((detail) => {
    const sendStatus = listSendStatusById.get(detail.id)
    return sendStatus === undefined ||
      sendStatus === BiddingBidSendStatus.UNSPECIFIED ||
      sendStatus === detail.sendStatus
      ? detail
      : { ...detail, sendStatus }
  })
}

/** 観測の時計だけを比べる。操作日時や開いている入力は置き換えない。 */
function isSyncUpdatedSince(
  detail: ApprovalStateOf,
  item: ApprovalStateOf,
): boolean {
  const current = item.syncUpdatedAt
  const previous = detail.syncUpdatedAt
  return (
    current !== undefined &&
    (previous === undefined ||
      current.seconds > previous.seconds ||
      (current.seconds === previous.seconds && current.nanos > previous.nanos))
  )
}
